/* docChasing.js — Iter-284 / Workstream #4
 *
 * Document-request chasing engine. Pure functions, no React,
 * deterministic — given a request payload and a clock, the engine
 * returns:
 *
 *   • `getChaseStateFor(request, nowIso?)`
 *       → { sent: [{ level, sentAt }], pending: [{ level, dueAt }] }
 *
 *   • `getPendingChasesFor(request, nowIso?)`
 *       → [{ level, dueAt, copy }]  — chases that SHOULD have fired
 *                                       by now but haven't yet
 *
 *   • `chaseActivityRowFor({ dealId, request, level, sentAt })`
 *       → activity row to append to deal.activity
 *
 *   • `summariseChasesForDeal(requests, nowIso?)`
 *       → { outstanding: N, chased: N, finalChased: N }
 *
 * Chase ladder per the iter-284 brief:
 *   T+3 (level 1) — first reminder
 *   T+5 (level 2) — second reminder
 *   T+7 (level 3) — final escalation
 *
 * Levels are CUMULATIVE: a request 8 days old should have all
 * three chases on record. The engine reads
 * `request.chase_history` (an array of `{ level, sent_at }`
 * objects) so re-runs are idempotent.
 *
 * The engine is flag-gated at the integration sites via
 * `isDocChasingEnabled()` so the pilot can ship without the
 * auto-chases firing until ready.
 */

export const CHASE_LADDER = Object.freeze([
  Object.freeze({ level: 1, daysAfter: 3, label: "First reminder",  shortLabel: "T+3" }),
  Object.freeze({ level: 2, daysAfter: 5, label: "Second reminder", shortLabel: "T+5" }),
  Object.freeze({ level: 3, daysAfter: 7, label: "Final escalation",shortLabel: "T+7" }),
]);

const MS_PER_DAY = 86_400_000;

function _parseIso(s) {
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

/* ─── Chase state resolver ──────────────────────────────────────
 *
 * Returns the canonical chase state for a single request:
 *   • `sent`    — every chase the request already has on record.
 *   • `pending` — every chase whose `daysAfter` window has
 *                 elapsed but isn't yet in `chase_history`.
 *
 * Resolved requests never report pending chases.
 */
export function getChaseStateFor(request, nowIso) {
  if (!request) return { sent: [], pending: [] };
  const status = String(request.status || "").toLowerCase();
  const isOpen = status === "pending" || status === "requested" || status === "outstanding" || status === "";
  const now = _parseIso(nowIso) || Date.now();
  const createdAt = _parseIso(request.created_at) || now;
  const ageDays = Math.max(0, (now - createdAt) / MS_PER_DAY);

  const history = Array.isArray(request.chase_history) ? request.chase_history : [];
  const sent = history
    .map((h) => ({
      level: Number(h.level),
      sentAt: h.sent_at || h.sentAt || null,
    }))
    .filter((h) => Number.isFinite(h.level));

  if (!isOpen) return { sent, pending: [] };

  const sentLevels = new Set(sent.map((h) => h.level));
  const pending = CHASE_LADDER
    .filter((step) => ageDays >= step.daysAfter && !sentLevels.has(step.level))
    .map((step) => ({
      level: step.level,
      dueAt: new Date(createdAt + step.daysAfter * MS_PER_DAY).toISOString(),
      copy:  step.label,
    }));
  return { sent, pending };
}

/* ─── Convenience ──────────────────────────────────────────────── */
export function getPendingChasesFor(request, nowIso) {
  return getChaseStateFor(request, nowIso).pending;
}

/* ─── Activity row builder ─────────────────────────────────────── */
export function chaseActivityRowFor({ dealId, request, level, sentAt }) {
  const step = CHASE_LADDER.find((s) => s.level === level);
  const docLabel = request?.doc_label || request?.title || "document";
  const recipient = request?.requested_from_role || request?.requested_from_name || "participant";
  const stepCopy = step ? step.label : `Reminder L${level}`;
  return {
    id:         `chase-${dealId}-${request?.id || "req"}-L${level}-${Date.now()}`,
    type:       "chase",
    kind:       "doc_request_chased",
    label:      `${stepCopy} sent: ${docLabel} chased with ${recipient}`,
    title:      `${stepCopy} sent`,
    actor_role: "Automation",
    created_at: sentAt || new Date().toISOString(),
    source:     "doc-chasing",
    request_id: request?.id || null,
    chase_level: level,
  };
}

/* ─── Per-deal summary ─────────────────────────────────────────── */
export function summariseChasesForDeal(requests, nowIso) {
  const list = Array.isArray(requests) ? requests : [];
  let outstanding = 0, chased = 0, finalChased = 0;
  for (const r of list) {
    const status = String(r?.status || "").toLowerCase();
    const isOpen = status === "pending" || status === "requested" || status === "outstanding" || status === "";
    if (!isOpen) continue;
    outstanding++;
    const state = getChaseStateFor(r, nowIso);
    if (state.sent.length > 0) chased++;
    if (state.sent.some((c) => c.level >= 3)) finalChased++;
  }
  return { outstanding, chased, finalChased };
}

/* ─── Feature-flag helper ──────────────────────────────────────── */
export function isDocChasingEnabled() {
  return String(
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_DOC_CHASING_V1)
      || ""
  ).toLowerCase() === "true";
}
